/**
 * ChordPro language support for CodeMirror 6
 *
 * Provides syntax highlighting, folding, and other editor features
 * for the ChordPro chord sheet format.
 */
import {
  foldInside,
  foldNodeProp,
  LanguageSupport,
  LRLanguage,
} from "@codemirror/language";
import { styleTags, tags as t } from "@lezer/highlight";
import { parser } from "./parser.js";

/**
 * Parser configured with syntax highlighting and folding metadata
 */
const chordproParser = parser.configure({
  props: [
    styleTags({
      // Comments in muted gray
      Comment: t.lineComment,

      // Directives - keyword style for names, string for values
      DirectiveName: t.keyword,
      DirectiveValue: t.string,
      directiveText: t.string,

      // Chords - the main musical element, use special tag
      Chord: t.tagName,
      ChordContent: t.tagName,

      // Annotations - performance notes
      Annotation: t.annotation,
      annotationText: t.annotation,

      // Selectors for conditional directives
      Selector: t.operator,
      word: t.variableName,

      // Meta ternaries - embedded expressions
      MetaTernary: t.special(t.string),
      TernaryVariable: t.variableName,
      TernaryTest: t.string,
      ternaryTestText: t.string,
      TernaryBranch: t.string,
      ternaryBranchText: t.string,

      // Lyrics - regular content
      Lyrics: t.content,

      // Punctuation
      "{ }": t.brace,
      "[ ]": t.squareBracket,
      ":": t.punctuation,
      "|": t.separator,
      "=": t.operator,

      // Escaped characters
      escapedChar: t.escape,
    }),

    // Folding support - fold directive content
    foldNodeProp.add({
      Directive: foldInside,
    }),
  ],
});

/**
 * ChordPro language definition for CodeMirror
 */
export const chordproLanguage = LRLanguage.define({
  name: "chordpro",
  parser: chordproParser,
  languageData: {
    commentTokens: { line: "#" },
    closeBrackets: {
      brackets: ["[", "{", "("],
    },
  },
});

/**
 * ChordPro language support for CodeMirror 6
 *
 * Usage:
 * ```tsx
 * import { chordpro } from "@bandwith/lezer-chordpro";
 * import CodeMirror from "@uiw/react-codemirror";
 *
 * <CodeMirror extensions={[chordpro()]} />
 * ```
 */
export function chordpro(): LanguageSupport {
  return new LanguageSupport(chordproLanguage);
}

// Re-export for advanced usage
export { chordproParser };
